import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from '../services/store';
import { AppRoutes } from '../navigation/routes';
import { getIsLoggedIn } from 'src/shared/redux-state/src';
import { useCallback, useEffect, useRef, useState } from 'react';

// Define the flow steps
const FLOW_STEPS = {
    VERIFY_EMAIL: {
        route: AppRoutes.VERIFY_EMAIL,
        next: [AppRoutes.LOGIN, AppRoutes.VERIFY_OTP], // Can go to either login (active account) or OTP (inactive)
        previous: [],
    },
    LOGIN: {
        route: AppRoutes.LOGIN,
        next: [AppRoutes.VERIFY_OTP], // For MFA or forgot password
        previous: [AppRoutes.VERIFY_EMAIL],
    },
    VERIFY_OTP: {
        route: AppRoutes.VERIFY_OTP,
        next: [AppRoutes.VERIFY_DOB, AppRoutes.HOME], // Either continue activation or complete MFA
        previous: [AppRoutes.VERIFY_EMAIL, AppRoutes.LOGIN],
    },
    VERIFY_DOB: {
        route: AppRoutes.VERIFY_DOB,
        next: [AppRoutes.SET_PASSWORD],
        previous: [AppRoutes.VERIFY_OTP],
    },
    SET_PASSWORD: {
        route: AppRoutes.SET_PASSWORD,
        next: [AppRoutes.HOME],
        previous: [AppRoutes.VERIFY_DOB],
    },
};

const LoginStepperGuard = () => {
    const location = useLocation();
    const isLoggedIn = useAppSelector(getIsLoggedIn);
    const [completedSteps, setCompletedSteps] = useState<Array<string>>([]);
    const [canAccess, setCanAccess] = useState<boolean>(true);
    const completedStepsRef = useRef<Array<string>>(completedSteps);
    
    const computeCompletedSteps = useCallback(() => {
        const { email, isMFACall = false, sessionId, isAccountActive = false } = location.state || {};
        let newCompletedSteps: string[] = [...completedStepsRef.current];

        // If we have email in state, email verification is completed
        if (email) {
            if (!newCompletedSteps.includes(AppRoutes.VERIFY_EMAIL)) {
                newCompletedSteps.push(AppRoutes.VERIFY_EMAIL);
            }
        }

        // If we have sessionId, it means either:
        // 1. Login was completed (for active accounts)
        // 2. Email verification was completed (for inactive accounts)
        if (sessionId) {
            if (isAccountActive && !newCompletedSteps.includes(AppRoutes.LOGIN)) {
                newCompletedSteps.push(AppRoutes.LOGIN);
            }
        }

        // If isMFACall is true, it means login was completed
        if (isMFACall) {
            if (!newCompletedSteps.includes(AppRoutes.LOGIN)) {
                newCompletedSteps.push(AppRoutes.LOGIN);
            }
        }

        // If we're on DOB or password screen, OTP verification must be completed
        if (location.pathname === AppRoutes.VERIFY_DOB ||
            location.pathname === AppRoutes.SET_PASSWORD) {
            if (!newCompletedSteps.includes(AppRoutes.VERIFY_OTP)) {
                newCompletedSteps.push(AppRoutes.VERIFY_OTP);
            }
        }

        // If we're on password screen, DOB verification must be completed
        if (location.pathname === AppRoutes.SET_PASSWORD) {
            if (!newCompletedSteps.includes(AppRoutes.VERIFY_DOB)) {
                newCompletedSteps.push(AppRoutes.VERIFY_DOB);
            }
        }

        return newCompletedSteps;
    }, [location.pathname, location.state]);

    const checkAccess = useCallback(() => {
        const currentPath = location.pathname;
        const { isMFACall = false, isAccountActive = false } = location.state || {};
        const currentSteps = completedStepsRef.current;

        // Always allow access to initial step
        if (currentPath === AppRoutes.VERIFY_EMAIL) {
            return true;
        }

        // For login page
        if (currentPath === AppRoutes.LOGIN) {
            return currentSteps.includes(AppRoutes.VERIFY_EMAIL) && isAccountActive;
        }

        // For OTP verification
        if (currentPath === AppRoutes.VERIFY_OTP) {
            const isFromLogin = currentSteps.includes(AppRoutes.LOGIN) && isMFACall;
            const isFromEmail = currentSteps.includes(AppRoutes.VERIFY_EMAIL) && !isAccountActive;
            return isFromLogin || isFromEmail;
        }

        // For DOB verification
        if (currentPath === AppRoutes.VERIFY_DOB) {
            return currentSteps.includes(AppRoutes.VERIFY_OTP) && !isAccountActive;
        }

        // For password setup
        if (currentPath === AppRoutes.SET_PASSWORD) {
            return (currentSteps.includes(AppRoutes.VERIFY_DOB) && !isAccountActive) ||
                (currentSteps.includes(AppRoutes.VERIFY_OTP));
        }

        return false;
    }, [location.pathname, location.state]);

    useEffect(() => {
        const newCompletedSteps = computeCompletedSteps();
        if (JSON.stringify(newCompletedSteps) !== JSON.stringify(completedStepsRef.current)) {
            completedStepsRef.current = newCompletedSteps;
            setCompletedSteps(newCompletedSteps);
        }
    }, [computeCompletedSteps]);

    useEffect(() => {
        const hasAccess = checkAccess();
        setCanAccess(hasAccess);
    }, [checkAccess, completedSteps]);

    // If user is already authenticated, redirect to home
    if (isLoggedIn) {
        return <Navigate to={AppRoutes.HOME} replace />;
    }

    const currentPath = location.pathname;
    const currentFlowStep = Object.values(FLOW_STEPS).find(
        step => step.route === currentPath
    );

    // If trying to access an invalid route
    if (!currentFlowStep) {
        return <Navigate to={AppRoutes.VERIFY_EMAIL} replace />;
    }

    // If user can't access this step, redirect to appropriate step
    if (!canAccess) {
        if (!completedStepsRef.current.includes(AppRoutes.VERIFY_EMAIL)) {
            return <Navigate to={AppRoutes.VERIFY_EMAIL} replace />;
        }

        const { isAccountActive = false } = location.state || {};
        if (isAccountActive && !completedStepsRef.current.includes(AppRoutes.LOGIN)) {
            return <Navigate to={AppRoutes.LOGIN} replace />;
        }

        return <Navigate to={AppRoutes.VERIFY_EMAIL} replace />;
    }

    return <Outlet />;
};

export default LoginStepperGuard;